import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  CowMenu,
  Flag,
  Clipboard,
  IconManagement,
} from "../../../../assets/svg";
import { AnimalsManagement } from "../../../../components/AnimalsManagement";
import { Empty } from "../../../../components/Empty";
import { CreateBatch } from "../../../../components/CreateBatch";
import { ModalFlock } from "../../../../components/ModalFlock";
import { HeaderTitle } from "../../../../components/HeaderTitle";
import { useAuth } from "../../../../hooks/useAuth";
import { useNotification } from "../../../../hooks/useNotification";
import { useTranslate } from "../../../../hooks/useTranslate";
import {
  allAnimals,
  getAllBatchs,
  createBatch,
  getAllFlocks,
  createFlock,
} from "../../../../services/animals";
import {
  IAnimal,
  IBatch,
  ICreateBatchOrFlock,
  IFlock,
  IResponseBatchOrFlockAxios,
} from "../../interface";

import { ContentAnimals } from "./styles";
import { Button, Stack, SvgIcon } from "@mui/material";
import FabMenu from "../../../../components/FabMenu";
import { useModal } from "../../../../hooks/useModal";
import { CreateFlock } from "../../../../components/CreateFlock";

const ListAnimals = () => {
  const modal = useModal();
  const navigate = useNavigate();
  const { errorNotification, successNotification } = useNotification();
  const { translate } = useTranslate();
  const { propertiesIds } = useAuth();

  const [animals, setAnimals] = useState<IAnimal[]>([]);
  const [batchs, setBatchs] = useState<IBatch[]>([]);
  const [flocks, setFlocks] = useState<IFlock[]>([]);
  const [animalsWithOutBatch, setAnimalsWithOutBatch] = useState<IAnimal[]>([]);
  const [animalsWithOutFlock, setAnimalsWithOutFlock] = useState<IAnimal[]>([]);
  const [loading, setLoading] = useState(false);
  const [openModalFlock, setOpenModalFlock] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const itemsFab = [
    {
      label: translate("animals.fab_label_1"),
      icon: <CowMenu />,
      cb: () => {
        navigateUser("/animals/create");
      },
    },
    {
      label: translate("animals.fab_label_2"),
      icon: <Clipboard />,
      cb: () => {
        modal.open({
          title: translate("animals.title_new_batch"),
          content: (
            <CreateBatch
              animals={animalsWithOutBatch}
              onSubmit={sendNewBatch}
            />
          ),
          options: {
            maxWidth: "sm",
            fullWidth: true,
          },
        });
        // setOpenModalBatch(true);
      },
    },
    {
      label: translate("animals.fab_label_3"),
      icon: <Flag />,
      cb: () => {
        modal.open({
          title: translate("animals.title_new_flock"),
          content: (
            <CreateFlock
              animals={animalsWithOutBatch}
              onSubmit={sendNewFlock}
            />
          ),
          options: {
            maxWidth: "sm",
            fullWidth: true,
          },
        });
      },
    },
    {
      label: translate("animals.fab_label_4"),
      icon: <IconManagement />,
      cb: () => {
        navigateUser("/managements/create");
      },
    },
  ];

  const navigateUser = (url: string) => {
    navigate(url);
  };

  const loadAllFlocks = useCallback(
    async (id: string) => {
      try {
        const data = await getAllFlocks(id);

        const flocksFormatted = data.map(
          ({ id, name, animal }: IResponseBatchOrFlockAxios) => ({
            id,
            name,
            animals: animal.data,
            count: animal.meta.records,
          })
        );

        setFlocks(flocksFormatted);
      } catch {
        errorNotification(translate("animals.error_load_flock"));
      }
    },
    [errorNotification, translate]
  );

  const loadAllBatchs = useCallback(
    async (id: string) => {
      try {
        const data = await getAllBatchs(id);

        const batchsFormatted = data.map(
          ({ id, name, animal }: IResponseBatchOrFlockAxios) => ({
            id,
            name,
            animals: animal.data,
            count: animal.meta.records,
          })
        );

        setBatchs(batchsFormatted);
      } catch {
        errorNotification(translate("animals.error_load_batch"));
      }
    },
    [errorNotification, translate]
  );

  const loadAllAnimals = useCallback(async (id: string) => {
    const animals = await allAnimals(id);

    const animalsWithOutBatch: IAnimal[] = [];
    const animalsWithOutFlock: IAnimal[] = [];

    animals.forEach((animal) => {
      if (animal?.batch == null) animalsWithOutBatch.push(animal);
      if (animal?.flock == null) animalsWithOutFlock.push(animal);
    });

    setAnimalsWithOutBatch(animalsWithOutBatch);
    setAnimalsWithOutFlock(animalsWithOutFlock);
    setAnimals(animals);
  }, []);

  const boot = useCallback(async () => {
    try {
      setLoading(true);
      await loadAllAnimals(propertiesIds[0]);
      await loadAllBatchs(propertiesIds[0]);
      await loadAllFlocks(propertiesIds[0]);
    } catch {
      errorNotification(translate("animals.error_load_animals"));
    } finally {
      setLoading(false);
    }
  }, [
    errorNotification,
    loadAllAnimals,
    loadAllBatchs,
    loadAllFlocks,
    propertiesIds,
    translate,
  ]);

  const sendNewBatch = async (name: string, animals: string[]) => {
    try {
      const body: ICreateBatchOrFlock = {
        name,
        farm_id: propertiesIds[0],
        animals_id: animals.map((id) => ({ id })),
      };

      await createBatch(body);
      successNotification(translate("animals.success_create_batch"));
      await boot();
      searchParams.set("active", "batch");
      setSearchParams(searchParams);
    } catch {
      errorNotification(translate("animals.error_create_batch"));
    }
  };

  const sendNewFlock = async (name: string, animals: string[]) => {
    try {
      const body: ICreateBatchOrFlock = {
        name,
        farm_id: propertiesIds[0],
        animals_id: animals.map((id) => ({ id })),
      };

      await createFlock(body);
      setOpenModalFlock(false);
      successNotification(translate("animals.success_create_flock"));
      await boot();
      searchParams.set("active", "flock");
      setSearchParams(searchParams);
    } catch {
      errorNotification(translate("animals.error_create_flock"));
    }
  };

  const ready = useRef(false);

  useEffect(() => {
    if (!ready.current) {
      ready.current = true;
      boot();
    }
  }, [boot]);

  // if (loading) return <Spin size="large" tip="Loading..." />;

  return (
    <Stack spacing={4}>
      <HeaderTitle title={translate("animals.title_list")} />

      <ModalFlock
        visible={openModalFlock}
        onCancel={() => {
          setOpenModalFlock(false);
        }}
        animals={animalsWithOutFlock}
        batchs={batchs}
        cb={sendNewFlock}
      />
      {/* <FabButton
        active={activeFab}
        items={itemsFab}
        changeStatusFabButton={() => {
          if (propertiesIds.length === 0) {
            errorNotification(translate("animals.error_user_without_farmer"));
            navigate("/properties");
            return;
          }

          setActiveFab(!activeFab);
        }}
      /> */}
      <FabMenu>
        {itemsFab.map((item, key) => (
          <Button
            key={key}
            variant="contained"
            onClick={item.cb}
            endIcon={<SvgIcon>{item.icon}</SvgIcon>}
            sx={{ justifyContent: "space-between" }}
          >
            {item.label}
          </Button>
        ))}
      </FabMenu>

      {animals.length === 0 ? (
        <Empty
          title={translate("global.title_empty")}
          subTitle={translate("animals.description_empty")}
        />
      ) : (
        <ContentAnimals>
          <AnimalsManagement
            loading={loading}
            allAnimals={animals}
            batchs={batchs}
            flocks={flocks}
            cb={boot}
          />
        </ContentAnimals>
      )}
    </Stack>
  );
};

export { ListAnimals };
